// @flow
/**
 * Video Progress Service
 *
 * This service manages video progress tracking state and logic.
 * It's designed to break the circular dependency between video.store.js and actions.js
 * by centralizing progress-related state that was previously scattered across both files.
 *
 * IMPORTANT: This service must NOT import from:
 * - actions.js (would recreate circular dependency)
 * - RootStore (would recreate circular dependency via stores)
 *
 * All dependencies (vast, configStore) must be passed as parameters.
 */

import { sendOmidSessionFinish } from '../../../common/js/omid/omid';
import { formatEvent } from './event-formatter';
import VASTClient from './vast/vast-client';

// ============================================================================
// STATE
// ============================================================================

let lastProgress: number = 0;
let isCompleted: boolean = false;
let fullVideoIsCompleted: boolean = false;

// Callbacks for quartile tracking (real duration)
// These are reset after being called to ensure one-time execution
let fullVideoDurationQuartileTracks = createQuartileCallbacks();

// ============================================================================
// PRIVATE HELPERS
// ============================================================================

interface QuartileCallbacks {
  firstquartile?: () => void;
  midpoint?: () => void;
  thirdquartile?: () => void;
  complete?: () => void;
}

function createQuartileCallbacks(): QuartileCallbacks {
  return {};
}

function triggerEvents(events: Array<keyof QuartileCallbacks>) {
  events.forEach(event => {
    const callback = fullVideoDurationQuartileTracks[event];
    if (callback) {
      callback();
      fullVideoDurationQuartileTracks[event] = undefined;
    }
  });
}

// ============================================================================
// PUBLIC API - State Accessors
// ============================================================================

export function getLastProgress(): number {
  return lastProgress;
}

export function getIsCompleted(): boolean {
  return isCompleted;
}

export function getFullVideoIsCompleted(): boolean {
  return fullVideoIsCompleted;
}

// ============================================================================
// PUBLIC API - Progress Tracking
// ============================================================================

/**
 * Tracks video progress by updating lastProgress and vast progress.
 * Returns true if completion should be triggered.
 *
 * @param progress - Current video progress in seconds
 * @param vast - VASTClient instance
 * @returns Whether completion should be triggered (progress >= billedDuration)
 */
export function trackProgress(progress: number, vast: VASTClient): boolean {
  lastProgress = progress;
  vast.setProgress(progress);

  // Check if we should trigger completion
  return !isCompleted && progress >= vast.getBilledDuration();
}

/**
 * Marks the video as completed.
 * Should be called by onComplete in actions.js.
 */
export function markAsCompleted(): void {
  isCompleted = true;
}

// ============================================================================
// PUBLIC API - Full Video Duration Quartile Tracking
// ============================================================================

/**
 * Initializes the quartile callbacks with the provided event sender.
 * Must be called before trackRealVideoProgress.
 *
 * @param onEvent - Function to send video events (from actions.js)
 */
export function initializeQuartileCallbacks(
  onEvent: (event: string) => void
): void {
  fullVideoDurationQuartileTracks = {
    firstquartile: () => {
      onEvent('firstquartile_real');
    },
    midpoint: () => {
      onEvent('midpoint_real');
    },
    thirdquartile: () => {
      onEvent('thirdquartile_real');
    },
    complete: () => {
      onEvent(
        formatEvent({ event: 'complete_real', options: { viewRateReal: 100 } })
      );
      sendOmidSessionFinish();
    }
  };
}

/**
 * Tracks progress against full video duration for "real" quartile stats.
 * This handles the enableRealQuartiles feature and complete_real event.
 *
 * @param progress - Current video progress in seconds
 * @param vastDuration - Full video duration from VAST
 * @param enableRealQuartiles - Whether real quartile tracking is enabled
 */
export function trackRealVideoProgress(
  progress: number,
  vastDuration: number,
  enableRealQuartiles: boolean
): void {
  if (!enableRealQuartiles) {
    return;
  }

  if (progress >= Math.round(25 * vastDuration) / 100) {
    triggerEvents(['firstquartile']);
  }
  if (progress >= Math.round(50 * vastDuration) / 100) {
    triggerEvents(['firstquartile', 'midpoint']);
  }
  if (progress >= Math.round(75 * vastDuration) / 100) {
    triggerEvents(['firstquartile', 'midpoint', 'thirdquartile']);
  }

  // safe guard to ensure we don't miss quartile events (short video)
  if (progress >= vastDuration - 1) {
    triggerEvents(['firstquartile', 'midpoint', 'thirdquartile']);
  }
}

export function trackRealVideoComplete(): void {
  fullVideoIsCompleted = true;
  triggerEvents(['complete']);
}

// ============================================================================
// PUBLIC API - Reset (for testing or reinitialization)
// ============================================================================

/**
 * Resets all progress tracking state.
 * Useful for testing or when the ad context changes.
 */
export function resetProgressState(): void {
  lastProgress = 0;
  isCompleted = false;
  fullVideoIsCompleted = false;
  fullVideoDurationQuartileTracks = createQuartileCallbacks();
}
